<script setup lang="ts">
import {
  useActiveBanners,
  useCartQuery,
  useNavQuery,
  useWishlistQuery,
} from "~~/app/composables/queries/useStoreApi";
import type { NavItem } from "~~/shared/types/store";

const staticLinks: NavItem[] = [
  { label: "Marki", slug: "marki", href: "/marki", children: [] },
  { label: "O nas", slug: "o-nas", href: "/about", children: [] },
  { label: "Kontakt", slug: "kontakt", href: "/contact", children: [] },
];

const brand = useBrand();
// Tryb katalogowy chowa koszyk, konto i ulubione — w tym sklepie ich nie ma.
const { sellingEnabled } = useCatalogOnly();
const route = useRoute();
const { data: nav } = useNavQuery();
const { data: cart } = useCartQuery();
const { data: wishlist } = useWishlistQuery();

const nowosciLink: NavItem = {
  label: "Nowości",
  slug: "nowosci",
  href: "/nowosci",
  children: [],
};

// Znakowanie to sedno oferty sklepu katalogowego — klient B2B pyta o technikę
// wcześniej niż o sam produkt. W sklepach modowych ta strona nie istnieje,
// więc pozycja dochodzi tylko przy wyłączonej sprzedaży.
const znakowanieLink: NavItem = {
  label: "Techniki znakowania",
  slug: "techniki-znakowania",
  href: "/techniki-znakowania",
  children: [],
};

/**
 * Działy katalogu pod jedną pozycją z rozwijanym menu.
 *
 * Sklep katalogowy ma dziewięć działów. Wpuszczone do paska menu razem
 * z linkami statycznymi dawały CZTERNAŚCIE pozycji, a nagłówek potrzebował
 * 1347 px (menu 1174 + logo 100 + ikony 72). Hamburger włącza się dopiero
 * poniżej 961 px, więc każde okno między 961 a ~1400 px scrollowało stronę
 * w poziomie.
 *
 * Podniesienie progu hamburgera do 1400 px załatwiłoby scroll, ale zabrałoby
 * pełne menu laptopom 1366 px. Zwinięcie działów jest tańsze i zgodne
 * z monstera.pl — wzorcem, który wskazała klientka: tam w pasku stoją dwie
 * pozycje, a kategorie są w rozwijanym menu i w pasku ikon na stronie głównej.
 *
 * Mega-menu bierze `children` jako płaską listę (do 32 linków), więc dziewięć
 * działów mieści się bez obcięcia.
 */
const gadzetyLink = computed<NavItem>(() => ({
  label: "Gadżety",
  slug: "gadzety-dzialy",
  href: "/search",
  children: nav.value?.items ?? [],
}));

// Katalog (proles) nie ma marek: EasyItem nie przesyła producentów, więc
// /api/store/manufacturers na prod jest pusty (29.10.2026) i „Marki"
// prowadziły do pustej strony. Sklepy sprzedażowe zostają przy pełnej liście.
const catalogStaticLinks = staticLinks.filter((link) => link.slug !== "marki");

const allNavItems = computed<NavItem[]>(() =>
  sellingEnabled
    ? [nowosciLink, ...(nav.value?.items ?? []), ...staticLinks]
    : [nowosciLink, gadzetyLink.value, znakowanieLink, ...catalogStaticLinks],
);

const cartCount = computed(() => cart.value?.itemsCount || 0);
const wishlistCount = computed(() => wishlist.value?.products?.length || 0);

const headerBanners = useActiveBanners("displayBanner");

// Desktop mega menu
const activeSlug = ref<string | null>(null);
let closeTimer: ReturnType<typeof setTimeout> | null = null;

function openMega(slug: string) {
  if (closeTimer) clearTimeout(closeTimer);
  activeSlug.value = slug;
}
function scheduleMegaClose() {
  closeTimer = setTimeout(() => {
    activeSlug.value = null;
  }, 120);
}
function cancelMegaClose() {
  if (closeTimer) clearTimeout(closeTimer);
}

const activeItem = computed(
  () => allNavItems.value.find((i) => i.slug === activeSlug.value) ?? null,
);
const hasMega = computed(
  () => activeItem.value !== null && activeItem.value.children.length > 0,
);

// Mobile drawer
const isMobileOpen = ref(false);

// Search overlay
const searchOpen = ref(false);

watch(
  () => route.path,
  () => {
    isMobileOpen.value = false;
    activeSlug.value = null;
    searchOpen.value = false;
  },
);
</script>

<template>
  <!-- Promo strip — banery countdown z modułu PS pstbannercountdownpro.
       Poza <header> (sticky), żeby się NIE przyklejał — scroll-away w normalnym
       przepływie: znika przy scrollu w dół, wraca dopiero na samej górze. -->
  <!-- Bannery promocyjne tylko tam, gdzie da się kupić. W trybie katalogowym
       „odbierz zniżkę -30% z kodem" obiecuje rabat, którego nie ma gdzie użyć:
       nie ma koszyka ani pola na kod. Na sklepie gadżetów wyświetlał się pasek
       z kodami EXTRA3–EXTRA10 i boczny panel „TWOJE ZNIŻKI" — treść lavmaga. -->
  <template v-if="sellingEnabled">
    <!-- Kraj dostawy dla gości z zagranicy — tylko klient: HTML jest cache'owany na CDN,
         a kraj jest per odwiedzający (zob. DeliveryCountryBanner). -->
    <ClientOnly v-if="brand.deliveryCountryBanner">
      <DeliveryCountryBanner />
    </ClientOnly>
    <PromoCountdownBanner
      v-for="banner in headerBanners"
      :key="banner.id"
      :banner="banner"
    />
  </template>

  <header class="site-header" @mouseleave="scheduleMegaClose">
    <!-- Main nav -->
    <nav class="site-nav" @mouseenter="cancelMegaClose">
      <div class="site-nav__inner">
        <NuxtLink
          to="/"
          class="site-nav__logo"
          :aria-label="`${brand.name} – strona główna`"
        >
          <!-- Nazwę linku niesie aria-label; alt tylko dla marek, którym
               audyt zgłosił logo jako nieopisany obrazek (proles). -->
          <img
            :src="brand.logo.src"
            :alt="brand.logo.alt ?? ''"
            width="151"
            height="28"
            class="site-nav__logo-img"
          />
        </NuxtLink>

        <!-- Desktop links -->
        <ul class="site-nav__links desktop-only">
          <li
            v-for="item in allNavItems"
            :key="item.slug"
            @mouseenter="
              item.children.length ? openMega(item.slug) : (activeSlug = null)
            "
          >
            <NuxtLink
              :to="item.href"
              class="site-nav__link"
              :class="{ 'site-nav__link--active': activeSlug === item.slug }"
            >
              {{ item.label }}
            </NuxtLink>
          </li>
        </ul>

        <!-- Action icons -->
        <div class="site-nav__actions">
          <button
            type="button"
            class="site-nav__icon-btn"
            aria-label="Wyszukiwarka"
            @click="searchOpen = true"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </button>

          <NuxtLink
            v-if="sellingEnabled"
            to="/favorites"
            class="site-nav__icon-btn"
            aria-label="Ulubione"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path
                d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
              />
            </svg>
            <ClientOnly>
              <span v-if="wishlistCount > 0" class="site-nav__count">{{
                wishlistCount
              }}</span>
            </ClientOnly>
          </NuxtLink>

          <NuxtLink
            v-if="sellingEnabled"
            to="/account/orders"
            class="site-nav__icon-btn desktop-only"
            aria-label="Moje konto"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </NuxtLink>

          <NuxtLink
            v-if="sellingEnabled"
            to="/cart"
            class="site-nav__icon-btn"
            aria-label="Koszyk"
            data-cart-icon
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
            <ClientOnly>
              <span v-if="cartCount > 0" class="site-nav__count">{{
                cartCount
              }}</span>
            </ClientOnly>
          </NuxtLink>

          <!-- Tryb katalogowy: w miejscu koszyka stoi Lista Zapytania. Klient
               zbiera produkty i wysyła je jednym zapytaniem o wycenę. -->
          <QuoteListCounter
            v-else
            class="site-nav__icon-btn"
          />

          <!-- Mobile: hamburger / close -->
          <button
            class="site-nav__icon-btn mobile-only"
            type="button"
            :aria-expanded="isMobileOpen"
            aria-label="Menu"
            @click="isMobileOpen = !isMobileOpen"
          >
            <svg
              v-if="!isMobileOpen"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
            <svg
              v-else
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </nav>

    <!-- Desktop mega menu -->
    <div
      v-if="hasMega"
      @mouseenter="cancelMegaClose"
      @mouseleave="scheduleMegaClose"
    >
      <NavMegaMenu :item="activeItem!" />
    </div>

    <!-- Mobile drawer -->
    <NavMobileDrawer
      :items="allNavItems"
      :is-open="isMobileOpen"
      @close="isMobileOpen = false"
    />
  </header>
  <SearchOverlay v-model="searchOpen" />
</template>

<style scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--color-white);
}

/* Shipping strip */
.shipping-strip {
  background: var(--color-black);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 0 16px;
}
.shipping-strip__text {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-white);
  line-height: 1;
}

/* Site nav */
.site-nav {
  background: var(--color-white);
  border-bottom: 1px solid var(--color-border);
  height: 70px;
  display: flex;
  align-items: center;
}

.site-nav__inner {
  width: var(--container);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  height: 100%;
  padding: 0 20px;
}

.site-nav__logo {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  line-height: 1;
}

.site-nav__logo-img {
  display: block;
  height: 1.75rem;
  width: auto;
}

/* Logo Proles Concept jest znacznie mniej rozciągnięte w poziomie niż logotypy
   pozostałych marek (plik z marginesem 2,3:1, po przycięciu 30.10.2026 3,2:1,
   wobec ~5,4:1 — znak plus dwuwierszowy napis), więc
   przy wspólnej wysokości 28 px miałoby raptem 64 px szerokości i „CONCEPT"
   robiło się nieczytelne.

   Klientka poprosiła o jeszcze większe logo (Asana 1219566675747704): 44 px
   to wciąż było 100 px szerokości, a „CONCEPT" pod napisem miało ~4 px.

   Spotkanie 30.10.2026: „logo w headerze większe". Plik miał pusty margines
   — rysunek zajmował 56% wysokości viewBox, więc obrazek 56 px dawał ~31 px
   widocznego logo. Plik jest przycięty do rysunku (logo.svg, logo-rev.svg),
   a 44 px to teraz 44 px samego logo (~143 px szerokości, wcześniej ~102). */
[data-brand="proles"] .site-nav__logo-img {
  height: 2.75rem;
}

/* Spotkanie 30.10.2026: „powiększyć napisy w menu". 14 px cienkiej czcionki
   ginęło przy 125% skalowaniu Windows klientki; menu katalogu ma 5 pozycji,
   więc 16 px mieści się bez zmiany progu hamburgera. */
[data-brand="proles"] .site-nav__link {
  font-size: 1rem;
}

/* Logo DISCANTO to płaski pasek 8,8:1 (799×91). Przy wspólnej wysokości 28 px
   miałoby 246 px szerokości i rozpychało pasek menu, więc wymiarujemy je
   szerokością — tą samą, którą zajmuje logo lavmaga (151 px). aspect-ratio
   rezerwuje wysokość przed wczytaniem pliku (atrybuty <img> mają 151×28). */
[data-brand="discanto"] .site-nav__logo-img {
  width: 151px;
  height: auto;
  aspect-ratio: 799 / 91;
}

.site-nav__links {
  display: flex;
  align-items: center;
  /* Odstęp rampuje 20px→40px w paśmie 960–1260px — przy pełnych 40px 8 pozycji
     nie mieści się na średnich ekranach (powodowało poziomy scroll); ≥1260px
     wraca do 40px. Spięte z rampem marginesów --container w main.css. */
  gap: clamp(20px, calc(100vw * 0.0667 - 44px), 40px);
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  justify-content: center;
}

.site-nav__link {
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: normal;
  white-space: nowrap;
  transition: opacity 0.15s;
  padding-bottom: 2px;
}

.site-nav__link--active,
.site-nav__link.router-link-active {
  font-weight: var(--font-weight-bold);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.site-nav__link:hover {
  opacity: 0.7;
}

.site-nav__actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.site-nav__icon-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-black);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  transition: opacity 0.15s;
}
.site-nav__icon-btn:hover {
  opacity: 0.6;
}

.site-nav__count {
  position: absolute;
  top: -4px;
  right: -6px;
  background: var(--color-black);
  color: var(--color-white);
  font-size: 0.625rem;
  font-weight: var(--font-weight-bold);
  min-width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

@media (max-width: 960px) {
  .site-nav__links {
    display: none;
  }
  .site-nav__inner {
    justify-content: space-between;
  }
  .site-nav {
    height: 40px;
  }
  /* Proles: w 40-pikselowym pasku logo nie miało gdzie urosnąć (a między
     481 a 960 px 44-pikselowe logo wręcz wystawało poza pasek). Pasek 56 px
     z logo 44 px zachowuje proporcję z desktopu — logo na ~80% wysokości.
     Zmienną czyta też szuflada menu (`top`), więc zaczyna się tuż pod
     paskiem, a nie 134 px od góry z prześwitem na treść strony. */
  [data-brand="proles"] .site-header {
    --header-height: 56px;
  }
  [data-brand="proles"] .site-nav {
    height: var(--header-height);
  }
  /* Plik przycięty do rysunku (30.10.2026): 34 px to widoczne logo, wcześniej
     obrazek 44 px dawał ~24 px. */
  [data-brand="proles"] .site-nav__logo-img {
    height: 2.125rem;
  }
  /* Ikony akcji ~20% większe na mobile (były za małe) — lupka/ulubione/koszyk
     18→22px, hamburger 20→24px. Skala przez CSS nadpisuje width/height SVG. */
  .site-nav__icon-btn svg {
    width: 22px;
    height: 22px;
  }
  .site-nav__icon-btn.mobile-only svg {
    width: 24px;
    height: 24px;
  }
}

/* Małe telefony: odzyskaj boczne marginesy kontenera i zacieśnij odstępy,
   żeby logo + ikony akcji (oba o stałej szerokości) nie wymuszały poziomego
   scrolla. Wcześniej pasek przelewał się poniżej ~400px. Floor: 320px. */
@media (max-width: 480px) {
  .site-nav__inner {
    width: 100%;
    padding: 0 12px;
    gap: 12px;
  }
  .site-nav__actions {
    gap: 8px;
  }
  .site-nav__logo-img {
    height: 1.5rem;
  }

  /* Proles zostaje przy 34 px z reguły wyżej (silniejszy selektor): przy
     320 px logo ma 110 px, a ikony akcji z odstępami ~110 px — mieści się. */

  /* 129 px jak lavmag na telefonie, ale nie więcej niż zostawiają ikony akcji:
     przy 320 px pasek mieści logo do ~112 px (lavmag przelewa się tam o 17 px). */
  [data-brand="discanto"] .site-nav__logo-img {
    width: min(129px, calc(100vw - 208px));
  }
}
</style>
